import type { DiscoverGameMetadataOptionsResponse, DiscoverGamePatchRequest, DiscoverGameV2Response } from "libs/apiClient";
import { _ } from "i18n";
import SelectComponent, { type SelectOption } from "ui-kit/src/SelectComponent/SelectComponent";
import AdminMultiSelectComponent from "components/select/AdminMultiSelectComponent";
import { toDateTimeLocalInputValue, toUtcDateTimeString } from "common/src/utils/dateTimeFormatUtils";
import { formatDateTime } from "features/discover/functions";
import type { ReactNode } from "react";
import { useState } from "react";
import { useToast } from "ui-kit";
import { gameTypeLabel } from "./gameMetadataFormatters";
import { DiscoverGameBasicPatchField, isStartsOnLeftSelectValue, StartsOnLeftSelectValue } from "./constants";

type BasicGamePatch = Pick<DiscoverGamePatchRequest, DiscoverGameBasicPatchField | "requestedFields">;

type AgeCategory = NonNullable<DiscoverGamePatchRequest["ageCategory"]>;

interface BasicGameMetadataEditorsProps {
	gameData: DiscoverGameV2Response;
	metadataOptions?: DiscoverGameMetadataOptionsResponse;
	isSaving: boolean;
	canEditGameUploads: boolean;
	canEditVip: boolean;
	canEditHidden: boolean;
	canEditScore: boolean;
	onPatch: (patch: BasicGamePatch) => void;
	onCancel: () => void;
}

function toSelectOptions(options: DiscoverGameMetadataOptionsResponse["gameTypes"]): SelectOption[] {
	return options.map((option) => ({ value: option.value, label: option.label }));
}

interface NullableStringSelectProps {
	value: string | null;
	options: SelectOption[];
	isDisabled: boolean;
	onChange: (value: string | null) => void;
}

function NullableStringSelect({ value, options, isDisabled, onChange }: NullableStringSelectProps) {
	return (
		<SelectComponent
			value={options.find((option) => option.value === value) ?? null}
			items={options}
			isDisabled={isDisabled}
			handleChange={(option) => {
				if (option === null) {
					onChange(null);
					return;
				}
				if (typeof option.value === "string") {
					onChange(option.value);
				}
			}}
		/>
	);
}

function detailCell(title: string, content: ReactNode) {
	return (
		<div className="detailCell detailCell--editing">
			<div className="cellTitle">{title}</div>
			<div className="cellValue">
				<div className="cellContent">{content}</div>
			</div>
		</div>
	);
}

function booleanLabel(value: boolean): string {
	return value ? _("label.datagrid.yes") : _("label.datagrid.no");
}

interface BooleanFieldEditorProps {
	id: string;
	label: string;
	value: boolean;
	readOnlyValue: boolean;
	isEditable: boolean;
	isSaving: boolean;
	onChange: (value: boolean) => void;
}

function BooleanFieldEditor({
	id,
	label,
	value,
	readOnlyValue,
	isEditable,
	isSaving,
	onChange,
}: BooleanFieldEditorProps) {
	if (isEditable === false) {
		return booleanLabel(readOnlyValue);
	}

	return (
		<label className="cellCheckboxLabel" htmlFor={id}>
			<input
				id={id}
				className="cellInput"
				type="checkbox"
				checked={value}
				disabled={isSaving}
				onChange={(event) => onChange(event.currentTarget.checked)}
			/>
			{label}
		</label>
	);
}

function startsOnLeftFromGame(homeTeamStartsOnLeft: boolean | null): StartsOnLeftSelectValue {
	if (homeTeamStartsOnLeft === true) {
		return StartsOnLeftSelectValue.Home;
	}
	if (homeTeamStartsOnLeft === false) {
		return StartsOnLeftSelectValue.Away;
	}
	return StartsOnLeftSelectValue.Unset;
}

// The API types the scores as integers with PositiveOrZero, so decimals and negatives must be rejected here.
function isValidScoreInput(rawScore: string): boolean {
	if (rawScore === "") {
		return true;
	}
	const score = Number(rawScore);
	return Number.isInteger(score) && score >= 0;
}

function startsOnLeftToPatchValue(value: StartsOnLeftSelectValue): boolean | null {
	if (value === StartsOnLeftSelectValue.Home) {
		return true;
	}
	if (value === StartsOnLeftSelectValue.Away) {
		return false;
	}
	return null;
}

export function BasicGameMetadataEditors({
	gameData,
	metadataOptions,
	isSaving,
	canEditGameUploads,
	canEditVip,
	canEditHidden,
	canEditScore,
	onPatch,
	onCancel,
}: BasicGameMetadataEditorsProps) {
	const { addErrorToast } = useToast();
	const homeTeamLabel = _("discover.gameDetail.field.homeTeam");
	const awayTeamLabel = _("discover.gameDetail.field.awayTeam");
	const notSetLabel = _("discover.gameDetail.field.notSet");

	const gameTypeOptions = toSelectOptions(metadataOptions?.gameTypes ?? []);
	const leagueOptions = toSelectOptions(metadataOptions?.leagues ?? []);
	const ageCategoryOptions = toSelectOptions(metadataOptions?.ageCategories ?? []);
	const ownerOptions = toSelectOptions(metadataOptions?.owners ?? []);
	const ownerMultiSelectOptions = ownerOptions.map((option) => ({ value: String(option.value), label: option.label }));
	const startsOnLeftOptions: SelectOption[] = [
		{ value: StartsOnLeftSelectValue.Unset, label: notSetLabel },
		{ value: StartsOnLeftSelectValue.Home, label: homeTeamLabel },
		{ value: StartsOnLeftSelectValue.Away, label: awayTeamLabel },
	];

	const [date, setDate] = useState(toDateTimeLocalInputValue(gameData.date));
	const [homeScore, setHomeScore] = useState(gameData.home?.score?.toString() ?? "");
	const [awayScore, setAwayScore] = useState(gameData.away?.score?.toString() ?? "");
	const [gameType, setGameType] = useState(gameData.type ?? null);
	const [leagueId, setLeagueId] = useState<number | null>(
		gameData.league == null ? null : Number(gameData.league.id),
	);
	const [ageCategory, setAgeCategory] = useState<AgeCategory | null>(gameData.ageCategory);
	const [ownerIds, setOwnerIds] = useState(gameData.owners.map((owner) => Number(owner.id)));
	const [isVip, setIsVip] = useState(gameData.isVip);
	const [isHidden, setIsHidden] = useState(gameData.isHidden);
	const [isHiddenForScout, setIsHiddenForScout] = useState(gameData.isHiddenForScout);
	const [startsOnLeft, setStartsOnLeft] = useState<StartsOnLeftSelectValue>(
		startsOnLeftFromGame(gameData.homeTeamStartsOnLeft),
	);

	const startsOnLeftReadOnlyLabel = (() => {
		if (gameData.homeTeamStartsOnLeft === true) {
			return homeTeamLabel;
		}
		if (gameData.homeTeamStartsOnLeft === false) {
			return awayTeamLabel;
		}
		return "-";
	})();

	const handleSave = () => {
		const patch: BasicGamePatch = { requestedFields: [] };

		if (canEditGameUploads) {
			const utcDate = toUtcDateTimeString(date);
			// The API rejects a null date, so an empty input is only acceptable on a game that has no date yet.
			const canOmitDate = date === "" && gameData.date == null;
			if (utcDate === null && canOmitDate === false) {
				addErrorToast(_("discover.gameDetail.toast.invalidDate"));
				return;
			}

			patch.requestedFields.push(
				DiscoverGameBasicPatchField.Type,
				DiscoverGameBasicPatchField.LeagueId,
				DiscoverGameBasicPatchField.AgeCategory,
				DiscoverGameBasicPatchField.OwnerIds,
				DiscoverGameBasicPatchField.IsHiddenForScout,
				DiscoverGameBasicPatchField.HomeTeamStartsOnLeft,
			);
			if (utcDate !== null) {
				patch.requestedFields.push(DiscoverGameBasicPatchField.Date);
				patch.date = utcDate;
			}
			patch.type = gameType as BasicGamePatch["type"];
			patch.leagueId = leagueId;
			patch.ageCategory = ageCategory;
			patch.ownerIds = ownerIds;
			patch.isHiddenForScout = isHiddenForScout;
			patch.homeTeamStartsOnLeft = startsOnLeftToPatchValue(startsOnLeft);
		}

		if (canEditScore) {
			const hasInvalidScore = isValidScoreInput(homeScore) === false || isValidScoreInput(awayScore) === false;
			if (hasInvalidScore) {
				addErrorToast(_("discover.gameDetail.toast.invalidScore"));
				return;
			}

			patch.requestedFields.push(
				DiscoverGameBasicPatchField.HomeScore,
				DiscoverGameBasicPatchField.AwayScore,
			);
			patch.homeScore = homeScore === "" ? null : Number(homeScore);
			patch.awayScore = awayScore === "" ? null : Number(awayScore);
		}

		if (canEditVip) {
			patch.requestedFields.push(DiscoverGameBasicPatchField.IsVip);
			patch.isVip = isVip;
		}

		if (canEditHidden) {
			patch.requestedFields.push(DiscoverGameBasicPatchField.IsHidden);
			patch.isHidden = isHidden;
		}

		if (patch.requestedFields.length === 0) {
			addErrorToast(_("discover.gameDetail.toast.nothingToSave"));
			return;
		}

		onPatch(patch);
	};

	return (
		<>
			{detailCell(
				_("discover.gameDetail.field.date"),
				canEditGameUploads ? (
					<input
						className="cellInput basicGameMetadataEditors-dateInput"
						type="datetime-local"
						value={date}
						disabled={isSaving}
						onChange={(event) => setDate(event.currentTarget.value)}
					/>
				) : (
					formatDateTime(gameData.date)
				),
			)}
			{detailCell(
				_("discover.gameDetail.field.score"),
				canEditScore ? (
					<div className="basicGameMetadataEditors-scoreInputs">
						<input
							className="cellInput"
							type="number"
							min={0}
							step={1}
							value={homeScore}
							disabled={isSaving}
							onChange={(event) => setHomeScore(event.currentTarget.value)}
						/>
						<input
							className="cellInput"
							type="number"
							min={0}
							step={1}
							value={awayScore}
							disabled={isSaving}
							onChange={(event) => setAwayScore(event.currentTarget.value)}
						/>
					</div>
				) : (
					`${gameData.home?.score ?? "-"} : ${gameData.away?.score ?? "-"}`
				),
			)}
			{detailCell(
				_("discover.gameDetail.field.type"),
				canEditGameUploads ? (
					<NullableStringSelect
						value={gameType}
						options={gameTypeOptions}
						isDisabled={isSaving}
						onChange={setGameType}
					/>
				) : (
					gameTypeLabel(gameData.type, metadataOptions?.gameTypes)
				),
			)}
			{detailCell(
				_("discover.gameDetail.field.league"),
				canEditGameUploads ? (
					<SelectComponent
						value={leagueOptions.find((option) => Number(option.value) === leagueId) ?? null}
						items={leagueOptions}
						isCleareable
						isDisabled={isSaving}
						handleChange={(option) => {
							const nextLeagueId = option === null ? null : Number(option.value);
							if (nextLeagueId !== null && Number.isInteger(nextLeagueId) === false) {
								return;
							}
							setLeagueId(nextLeagueId);
						}}
					/>
				) : (
					gameData.league?.name ?? "-"
				),
			)}
			{detailCell(
				_("discover.gameDetail.field.ageCategory"),
				canEditGameUploads ? (
					<NullableStringSelect
						value={ageCategory}
						options={ageCategoryOptions}
						isDisabled={isSaving}
						onChange={(value) => setAgeCategory(value as AgeCategory | null)}
					/>
				) : (
					gameData.ageCategory ?? "-"
				),
			)}
			{detailCell(
				_("discover.gameDetail.field.owners"),
				canEditGameUploads ? (
					<AdminMultiSelectComponent
						items={ownerMultiSelectOptions}
						value={ownerMultiSelectOptions.filter((option) => ownerIds.includes(Number(option.value)))}
						isDisabled={isSaving}
						handleChange={(selectedOptions) => {
							const nextOwnerIds = selectedOptions
								.map((option) => Number(option.value))
								.filter((ownerId) => Number.isInteger(ownerId));
							setOwnerIds(nextOwnerIds);
						}}
					/>
				) : (
					gameData.owners.map((owner) => owner.name).join(", ") || "-"
				),
			)}
			{detailCell(
				_("discover.gameDetail.field.isVip"),
				<BooleanFieldEditor
					id="basic-game-isVip"
					label={_("discover.gameDetail.field.isVip")}
					value={isVip}
					readOnlyValue={gameData.isVip}
					isEditable={canEditVip}
					isSaving={isSaving}
					onChange={setIsVip}
				/>,
			)}
			{detailCell(
				_("discover.gameDetail.field.isHidden"),
				<BooleanFieldEditor
					id="basic-game-isHidden"
					label={_("discover.gameDetail.field.isHidden")}
					value={isHidden}
					readOnlyValue={gameData.isHidden}
					isEditable={canEditHidden}
					isSaving={isSaving}
					onChange={setIsHidden}
				/>,
			)}
			{detailCell(
				_("discover.gameDetail.field.isHiddenForScout"),
				<BooleanFieldEditor
					id="basic-game-isHiddenForScout"
					label={_("discover.gameDetail.field.isHiddenForScout")}
					value={isHiddenForScout}
					readOnlyValue={gameData.isHiddenForScout}
					isEditable={canEditGameUploads}
					isSaving={isSaving}
					onChange={setIsHiddenForScout}
				/>,
			)}
			{detailCell(
				_("discover.gameDetail.field.startsOnLeft"),
				canEditGameUploads ? (
					<SelectComponent
						value={startsOnLeftOptions.find((option) => option.value === startsOnLeft) ?? null}
						items={startsOnLeftOptions}
						isDisabled={isSaving}
						handleChange={(option) => {
							if (option === null || typeof option.value !== "string") {
								return;
							}
							if (isStartsOnLeftSelectValue(option.value) === false) {
								return;
							}
							setStartsOnLeft(option.value);
						}}
					/>
				) : (
					startsOnLeftReadOnlyLabel
				),
			)}
			<div className="basicGameMetadataEditors-saveAction form__footer">
				<button className="btn btn--white-red" type="button" disabled={isSaving} onClick={onCancel}>
					{_("form_edit_club_amateur.label.cancel_edit")}
				</button>
				<button className="btn" type="button" disabled={isSaving} onClick={handleSave}>
					{_("discover.gameDetail.btn.saveChanges")}
				</button>
			</div>
		</>
	);
}
